"use client";

/**
 * ChatbotWidget
 * Floating chatbot UI for landing pages that can answer questions about event content
 */

import { LandingPage } from "@/lib/database/landing-pages";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { getChatConversation } from "@/features/landing-page-v2/_logic/actions/chatbot/get-chat-conversation";
import {
  CHAT_ANSWER_RECEIVED_EVENT,
  CHAT_ERROR_EVENT,
  CHAT_OPENED_EVENT,
  CHAT_QUESTION_SUBMITTED_EVENT,
  CHAT_REQUEST_TIMEOUT_MS,
  resolveChatErrorCopy,
  type ChatErrorContext,
} from "@/features/landing-pages/_logic/chatbot-client";
import { parseInAppLink, type InAppLinkTarget } from "@/features/landing-pages/_logic/chat-link-target";
import { LinkifiedMessage } from "./LinkifiedMessage";
import { MessageCircle, RotateCw, Send, X, Sparkles } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useCallback, useState, useRef, useEffect } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { useMobile } from "@/hooks/use-mobile";

// Form / hotel-booking links are opened as an embedded modal (same as the page's
// SmartLink), not a new tab. Both modals are lazy-loaded so their (heavy) bundles
// only load when a visitor actually opens one from a chat reply.
const FormModal = dynamic(
  () => import("@/features/landing-page-v2/_components/public/FormModal").then((mod) => mod.FormModal),
  { ssr: false }
);
const HotelBookingModal = dynamic(
  () => import("@/features/landing-page-v2/_components/public/HotelBookingModal").then((mod) => mod.HotelBookingModal),
  { ssr: false }
);

interface ChatbotWidgetProps {
  /** V1 landing page (pass this OR pageId+pageName for V2) */
  landingPage?: LandingPage;
  /** V2 page ID (from event_landing_pages table) — used when landingPage is not provided */
  pageId?: number;
  /** Page name for welcome message — used when landingPage is not provided */
  pageName?: string;
  primaryColor?: string;
  externalOpen?: boolean;
  onExternalOpenHandled?: () => void;
  /** Called whenever the chat opens or closes, so the page can move floating controls out of the way */
  onOpenChange?: (isOpen: boolean) => void;
}

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  /** Set on a failed assistant bubble; carries the question so the retry button can resend it. */
  retryPrompt?: string;
}

const MIN_WIDTH = 320;
const MIN_HEIGHT = 400;
const DEFAULT_WIDTH = 480;
const DEFAULT_HEIGHT = 650;

/**
 * Returns the default welcome message shown when no persisted history exists.
 * @param resolvedName - Landing page name used in the greeting.
 * @returns Single welcome chat message.
 */
function buildWelcomeMessage(resolvedName: string): ChatMessage[] {
  return [
    {
      id: "welcome",
      role: "assistant",
      content: `Hi! I'm here to help answer questions about ${resolvedName}. What would you like to know?`,
      timestamp: new Date(),
    },
  ];
}

export default function ChatbotWidget({
  landingPage,
  pageId,
  pageName,
  primaryColor = "#5261EB",
  externalOpen,
  onExternalOpenHandled,
  onOpenChange,
}: ChatbotWidgetProps) {
  // Derived values — resolve V1 vs V2 props
  const resolvedName = landingPage?.name || pageName || "this event";
  const hasPageIdentifier = !!(landingPage || pageId);

  // Hooks
  const posthog = usePostHog();
  const router = useRouter();

  // States
  const isMobile = useMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => buildWelcomeMessage(resolvedName));
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isInitialized, setIsInitialized] = useState(false);
  // In-app link opened from a chat reply (form or hotel-booking modal), or null when none is open.
  const [activeLink, setActiveLink] = useState<InAppLinkTarget | null>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  // Analytics — emit a chatbot event tagged with the page identifiers, so a failing
  // assistant surfaces in analytics instead of only in a session recording. Declared
  // before the effects because the external-open effect depends on it.
  const captureChatEvent = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      posthog?.capture(event, {
        landing_page_id: landingPage?.landing_page_id,
        v2_page_id: pageId,
        ...properties,
      });
    },
    [posthog, landingPage?.landing_page_id, pageId]
  );

  // Effects
  useEffect(() => {
    if (!pageId) return;

    let active = true;
    setIsHydrating(true);

    void getChatConversation(pageId)
      .then((conversation) => {
        if (!active) return;

        if (!conversation?.messages.length) {
          setMessages(buildWelcomeMessage(resolvedName));
          return;
        }

        setMessages(
          conversation.messages.map((message) => ({
            id: message.id,
            role: message.role,
            content: message.content,
            timestamp: new Date(message.createdAt),
          }))
        );
      })
      .catch((error) => {
        console.error("Failed to hydrate chat conversation:", error);
      })
      .finally(() => {
        if (active) {
          setIsHydrating(false);
        }
      });

    return () => {
      active = false;
    };
  }, [pageId, resolvedName]);

  useEffect(() => {
    if (isOpen && inputRef.current && !isMobile) {
      inputRef.current.focus();
    }
  }, [isOpen, isMobile]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && messagesContainerRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isMobile && isOpen) {
      const updateSize = () => {
        const vv = window.visualViewport;
        if (vv) {
          setSize({ width: vv.width, height: vv.height });
          setPosition({ x: vv.offsetLeft, y: vv.offsetTop });
        } else {
          setSize({ width: window.innerWidth, height: window.innerHeight });
          setPosition({ x: 0, y: 0 });
        }
      };

      const vv = window.visualViewport;
      if (vv) {
        vv.addEventListener("resize", updateSize);
        vv.addEventListener("scroll", updateSize);
      }
      window.addEventListener("resize", updateSize);
      updateSize();

      return () => {
        if (vv) {
          vv.removeEventListener("resize", updateSize);
          vv.removeEventListener("scroll", updateSize);
        }
        window.removeEventListener("resize", updateSize);
      };
    }
  }, [isMobile, isOpen]);

  useEffect(() => {
    if (isMobile && isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.height = "100%";

      return () => {
        const bodyTop = document.body.style.top;
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.height = "";
        if (bodyTop) {
          window.scrollTo(0, parseInt(bodyTop || "0") * -1);
        }
      };
    }
  }, [isMobile, isOpen]);

  useEffect(() => {
    const initializePosition = () => {
      if (isMobile) {
        // Mobile: fullscreen
        setSize({ width: window.innerWidth, height: window.innerHeight });
        setPosition({ x: 0, y: 0 });
        setIsInitialized(true);
        return;
      }

      const savedSize = localStorage.getItem("chatbot-size");
      let currentWidth = DEFAULT_WIDTH;
      let currentHeight = DEFAULT_HEIGHT;

      if (savedSize) {
        try {
          const s = JSON.parse(savedSize);
          currentWidth = s.width || DEFAULT_WIDTH;
          currentHeight = s.height || DEFAULT_HEIGHT;
          setSize({ width: currentWidth, height: currentHeight });
        } catch (e) {
          // Ignore parse errors
        }
      }

      // Anchor near the floating button (bottom-right) on init
      const x = window.innerWidth - currentWidth - 24;
      const y = window.innerHeight - currentHeight - 96;
      setPosition({ x: Math.max(0, x), y: Math.max(0, y) });

      setIsInitialized(true);
    };

    initializePosition();
  }, [isMobile]);

  // Handle external open trigger (from chat prompt button)
  useEffect(() => {
    if (externalOpen && !isOpen) {
      setHasBeenOpened(true);
      captureChatEvent(CHAT_OPENED_EVENT, { trigger: "external" });
      if (!isMobile) {
        const x = window.innerWidth - size.width - 24;
        const y = window.innerHeight - size.height - 96;
        setPosition({ x: Math.max(0, x), y: Math.max(0, y) });
      }
      setIsOpen(true);
      onExternalOpenHandled?.();
    }
  }, [externalOpen, isOpen, isMobile, size.width, size.height, onExternalOpenHandled, captureChatEvent]);

  useEffect(() => {
    if (isDragging && !isMobile) {
      const handleMouseMove = (e: MouseEvent) => {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;

        const updatedPosition = {
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        };

        setPosition(updatedPosition);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragStart, size, isMobile]);

  useEffect(() => {
    if (isResizing && !isMobile) {
      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;

        const newWidth = Math.max(MIN_WIDTH, resizeStart.width + deltaX);
        const newHeight = Math.max(MIN_HEIGHT, resizeStart.height + deltaY);

        const maxWidth = window.innerWidth - position.x;
        const maxHeight = window.innerHeight - position.y;

        const updatedSize = {
          width: Math.min(newWidth, maxWidth),
          height: Math.min(newHeight, maxHeight),
        };

        setSize(updatedSize);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        if (widgetRef.current) {
          const rect = widgetRef.current.getBoundingClientRect();
          const finalSize = {
            width: rect.width,
            height: rect.height,
          };
          localStorage.setItem("chatbot-size", JSON.stringify(finalSize));
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, resizeStart, position, isMobile]);

  // Handlers
  // Open form / hotel-booking links from a chat reply as an embedded modal (return true
  // to suppress the anchor's new-tab navigation); let all other links open normally.
  const handleLinkClick = useCallback((href: string): boolean => {
    const target = parseInAppLink(href);
    if (!target) return false;
    setActiveLink(target);
    return true;
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      setHasBeenOpened(true);
      captureChatEvent(CHAT_OPENED_EVENT, { trigger: "button" });
      if (!isMobile) {
        const x = window.innerWidth - size.width - 24;
        const y = window.innerHeight - size.height - 96;
        setPosition({ x: Math.max(0, x), y: Math.max(0, y) });
      }
    }
    setIsOpen(!isOpen);
  };

  // Read the API's `error` field so distinct failures map to distinct copy. A body
  // that is not JSON (e.g. a proxy error page) resolves to undefined and falls
  // through to the generic connection message.
  const readErrorCode = async (response: Response): Promise<string | undefined> => {
    try {
      const data = await response.json();
      return typeof data?.error === "string" ? data.error : undefined;
    } catch {
      return undefined;
    }
  };

  // Append a failed assistant bubble and record the failure in analytics. `question`
  // is attached only for retryable errors so the retry button can resend it.
  const appendChatError = (context: ChatErrorContext, question: string) => {
    captureChatEvent(CHAT_ERROR_EVENT, { status: context.status, timed_out: !!context.timedOut });
    const { copy, retryable } = resolveChatErrorCopy(context);
    const errorMessage: ChatMessage = {
      id: `error-${Date.now()}`,
      role: "assistant",
      content: copy,
      timestamp: new Date(),
      retryPrompt: retryable ? question : undefined,
    };
    setMessages((prev) => [...prev, errorMessage]);
  };

  // Send a question to the chatbot API and append the answer or a specific error
  // bubble. `history` is the conversation before this question, sent as context.
  // An AbortController with a client timeout below the route's 60s ceiling stops a
  // slow generation from leaving the typing dots up with no way to cancel.
  const runChat = async (question: string, history: ChatMessage[], source: "submit" | "retry") => {
    setIsLoading(true);
    captureChatEvent(CHAT_QUESTION_SUBMITTED_EVENT, { question_length: question.length, source });

    const startedAt = performance.now();
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, CHAT_REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch("/api/landing-pages/chatbot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          ...(landingPage ? { landingPageId: landingPage.landing_page_id } : { v2PageId: pageId }),
          message: question,
          conversationHistory: history.map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      if (!response.ok) {
        appendChatError({ status: response.status, errorCode: await readErrorCode(response) }, question);
        return;
      }

      const data = await response.json();
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: data.response || "I'm sorry, I couldn't process that request. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      // An action changed the viewer's records, so re-render the page with them.
      if (data.refresh) router.refresh();
      captureChatEvent(CHAT_ANSWER_RECEIVED_EVENT, { latency_ms: Math.round(performance.now() - startedAt) });
    } catch {
      // The catch fires on our own timeout abort and on genuine network failures alike.
      appendChatError({ status: null, timedOut }, question);
    } finally {
      clearTimeout(timeoutId);
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const question = inputValue.trim();
    if (!question || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: question,
      timestamp: new Date(),
    };

    setInputValue("");
    setMessages((prev) => [...prev, userMessage]);
    void runChat(question, messages, "submit");
  };

  // Resend a question whose first attempt failed: drop the error bubble and reuse
  // the conversation up to (but not including) the pending question.
  const handleRetry = (errorMessageId: string, question: string) => {
    if (isLoading) return;
    const withoutError = messages.filter((m) => m.id !== errorMessageId);
    setMessages(withoutError);
    void runChat(question, withoutError.slice(0, -1), "retry");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleDragStart = (e: React.MouseEvent) => {
    if (isMobile) return;
    if (widgetRef.current) {
      const rect = widgetRef.current.getBoundingClientRect();
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
    });
    setIsResizing(true);
  };

  // Render — need at least one page identifier to function
  if (!hasPageIdentifier) return null;

  return (
    <>
      <style>{`
        @keyframes typing-dot {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.7;
          }
          30% {
            transform: translateY(-4px);
            opacity: 1;
          }
        }
        @keyframes chat-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.6);
            opacity: 0;
          }
        }
        @keyframes chat-bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
        @keyframes chat-label-in {
          0% {
            opacity: 0;
            transform: translateX(8px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      {/* Backdrop Overlay - Mobile Only */}
      {isOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-49"
          onClick={handleToggle}
          style={{ touchAction: "manipulation" }}
          aria-hidden="true"
        />
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          ref={widgetRef}
          className={`fixed bg-white shadow-2xl flex flex-col z-50 border border-gray-200/50 overflow-hidden backdrop-blur-sm ${
            isMobile ? "rounded-none" : "rounded-2xl"
          }`}
          style={
            isMobile
              ? {
                  left: `${position.x}px`,
                  top: `${position.y}px`,
                  width: `${size.width}px`,
                  height: `${size.height}px`,
                  boxShadow: "none",
                  touchAction: "manipulation",
                }
              : {
                  left: `${position.x}px`,
                  top: `${position.y}px`,
                  width: `${size.width}px`,
                  height: `${size.height}px`,
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                  cursor: isDragging ? "grabbing" : "default",
                  touchAction: "manipulation",
                }
          }
        >
          {/* Subtle grid pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.1) 1px, transparent 1px)`,
              backgroundSize: "20px 20px",
            }}
          />
          {/* Header */}
          <div
            className={`flex items-center justify-between px-4 md:px-5 py-3 md:py-4 text-white relative select-none shrink-0 ${
              isMobile ? "" : "cursor-grab active:cursor-grabbing"
            }`}
            style={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
              touchAction: "manipulation",
            }}
            onMouseDown={handleDragStart}
          >
            <div className="absolute inset-0 bg-linear-to-r from-white/0 via-white/10 to-white/0 opacity-50" />
            <div className="flex items-center gap-3 relative z-10">
              <div className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-white/15 backdrop-blur-sm border border-white/20">
                <MessageCircle className="w-5 h-5" />
                <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-blue-300" fill="currentColor" />
              </div>
              <span className="font-semibold text-sm leading-tight">AI Chat Assistant</span>
            </div>
            <div onMouseDown={(e) => e.stopPropagation()}>
              <FetchButton
                onClick={handleToggle}
                variant="ghost"
                size="sm"
                iconOnly
                className="relative z-10 hover:bg-white/20 text-white border-none [&_svg]:text-white touch-manipulation"
                aria-label="Close chat"
              >
                <X className="w-5 h-5 text-white" />
              </FetchButton>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4 bg-linear-to-b from-white via-gray-50/20 to-gray-50/40 relative min-h-0"
            style={{
              touchAction: "pan-y",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              maxHeight: "100%",
            }}
          >
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] md:max-w-[80%] px-4 md:px-6 py-2.5 md:py-3 rounded-2xl ${
                    message.role === "user"
                      ? "bg-linear-to-br from-gray-50 to-gray-100 text-gray-900 rounded-br-md border border-gray-200/60 shadow-sm"
                      : "text-white rounded-bl-md shadow-md"
                  }`}
                  style={{
                    ...(message.role === "assistant"
                      ? {
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                          boxShadow: `0 4px 12px ${primaryColor}30, 0 2px 4px ${primaryColor}20`,
                        }
                      : {}),
                    touchAction: "manipulation",
                  }}
                >
                  <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ touchAction: "manipulation" }}>
                    <LinkifiedMessage content={message.content} onLinkClick={handleLinkClick} />
                  </div>
                  {message.retryPrompt && (
                    <button
                      type="button"
                      onClick={() => handleRetry(message.id, message.retryPrompt!)}
                      disabled={isLoading}
                      className="mt-2 flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-white/30 disabled:opacity-50 touch-manipulation"
                    >
                      <RotateCw className="w-3.5 h-3.5" />
                      Try again
                    </button>
                  )}
                </div>
              </div>
            ))}
            {(isLoading || isHydrating) && (
              <div className="flex justify-start">
                <div
                  className="px-4 py-3 rounded-2xl rounded-bl-md shadow-md"
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                    boxShadow: `0 4px 12px ${primaryColor}30, 0 2px 4px ${primaryColor}20`,
                  }}
                >
                  <div className="flex items-center gap-1 px-1">
                    <div
                      className="w-1.5 h-1.5 bg-white/80 rounded-full"
                      style={{
                        animation: "typing-dot 1.4s infinite ease-in-out",
                        animationDelay: "0ms",
                      }}
                    />
                    <div
                      className="w-1.5 h-1.5 bg-white/80 rounded-full"
                      style={{
                        animation: "typing-dot 1.4s infinite ease-in-out",
                        animationDelay: "160ms",
                      }}
                    />
                    <div
                      className="w-1.5 h-1.5 bg-white/80 rounded-full"
                      style={{
                        animation: "typing-dot 1.4s infinite ease-in-out",
                        animationDelay: "320ms",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={handleSubmit}
            className="p-3 md:p-4 border-t border-gray-200/60 bg-white/80 backdrop-blur-sm relative z-10 shrink-0"
            style={{ touchAction: "manipulation" }}
          >
            <div className="flex items-center gap-2">
              <FetchInput
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your question..."
                className="flex-1 rounded-full border-gray-300 bg-white/90 backdrop-blur-sm text-base"
                style={
                  {
                    "--tw-ring-color": primaryColor,
                    focusRingColor: primaryColor,
                    touchAction: "manipulation",
                  } as React.CSSProperties
                }
                disabled={isLoading || isHydrating}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading || isHydrating}
                className="p-2 md:p-2.5 rounded-full text-white transition-all duration-200 disabled:opacity-50 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg disabled:cursor-not-allowed touch-manipulation"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                  boxShadow: `0 2px 8px ${primaryColor}40`,
                  touchAction: "manipulation",
                }}
                aria-label="Send message"
              >
                {isLoading ? (
                  <div className="w-4 h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4 md:w-5 md:h-5" />
                )}
              </button>
            </div>
          </form>

          {/* Resize Handle */}
          {!isMobile && (
            <div
              className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize group"
              onMouseDown={handleResizeStart}
              aria-label="Resize chat window"
            >
              <div className="absolute bottom-0 right-0 w-0 h-0 border-l-16 border-l-transparent border-b-16 border-b-gray-300/40 group-hover:border-b-gray-400/60 transition-colors" />
              <div className="absolute bottom-1 right-1 w-0 h-0 border-l-12 border-l-transparent border-b-12 border-b-gray-400/60 group-hover:border-b-gray-500/80 transition-colors" />
            </div>
          )}
        </div>
      )}

      {/* Floating Button */}
      {!isMobile && (
        <div className="fixed bottom-4 right-4 md:right-6 z-50 flex items-center gap-3">
          {/* Label - hidden after first open */}
          {!isOpen && !hasBeenOpened && (
            <button
              onClick={handleToggle}
              className="bg-white rounded-full px-4 py-2 shadow-lg border border-gray-200 text-sm font-medium text-gray-700 whitespace-nowrap hover:bg-gray-50 transition-colors cursor-pointer"
              style={{ animation: "chat-label-in 0.4s ease-out 1s both" }}
            >
              Ask AI
            </button>
          )}
          <button
            onClick={handleToggle}
            className="relative w-14 h-14 rounded-full text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center hover:scale-110 active:scale-95 touch-manipulation"
            style={{
              backgroundColor: primaryColor,
              boxShadow: `0 4px 14px ${primaryColor}60`,
              touchAction: "manipulation",
              animation: isOpen || hasBeenOpened ? "none" : "chat-bounce 2s ease-in-out 2s 3",
            }}
            aria-label={isOpen ? "Close chat" : "Open chat"}
          >
            {/* Pulse ring - hidden after first open */}
            {!isOpen && !hasBeenOpened && (
              <span
                className="absolute inset-0 rounded-full"
                style={{
                  backgroundColor: primaryColor,
                  animation: "chat-pulse 2s ease-out infinite",
                }}
              />
            )}
            <span className="relative z-10">
              {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
            </span>
          </button>
        </div>
      )}
      {isMobile && !isOpen && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-3">
          {/* Label - hidden after first open */}
          {!hasBeenOpened && (
            <button
              onClick={handleToggle}
              className="bg-white rounded-full px-4 py-2 shadow-lg border border-gray-200 text-sm font-medium text-gray-700 whitespace-nowrap hover:bg-gray-50 transition-colors cursor-pointer touch-manipulation"
              style={{ animation: "chat-label-in 0.4s ease-out 1s both" }}
            >
              Ask AI
            </button>
          )}
          <button
            onClick={handleToggle}
            className="relative w-14 h-14 rounded-full text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center hover:scale-110 active:scale-95 touch-manipulation"
            style={{
              backgroundColor: primaryColor,
              boxShadow: `0 4px 14px ${primaryColor}60`,
              touchAction: "manipulation",
              animation: hasBeenOpened ? "none" : "chat-bounce 2s ease-in-out 2s 3",
            }}
            aria-label="Open chat"
          >
            {/* Pulse ring - hidden after first open */}
            {!hasBeenOpened && (
              <span
                className="absolute inset-0 rounded-full"
                style={{
                  backgroundColor: primaryColor,
                  animation: "chat-pulse 2s ease-out infinite",
                }}
              />
            )}
            <span className="relative z-10">
              <MessageCircle className="w-6 h-6" />
            </span>
          </button>
        </div>
      )}

      {/* In-app modals opened from chat reply links (form / hotel booking) */}
      {activeLink?.kind === "form" && (
        <FormModal
          isOpen
          onClose={() => setActiveLink(null)}
          formId={activeLink.formId}
          eventId={activeLink.eventId}
          primaryColor={primaryColor}
          inviteToken={activeLink.inviteToken}
        />
      )}
      {activeLink?.kind === "hotel-booking" && (
        <HotelBookingModal
          isOpen
          onClose={() => setActiveLink(null)}
          eventId={activeLink.eventId}
          bookingLinkId={activeLink.bookingLinkId}
        />
      )}
    </>
  );
}
